import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SelectData } from '../../shared/type/input';
import { RoutePaths } from '../../../core/constants/route-paths';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '../../shared/services/modal.service';
import { ContentUpdateService } from '../services/content-update.service';
import { ContentManagersComponent } from '../content-managers/content-managers.component';
import { SpinnerService } from '../../../core/services/spinner.service';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { REGEX_EMAIL } from '../../../core/constants/constants';
import { Content } from '../../contents/type/content';
import { IsValue } from '../../shared/validators/is-value.validator';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-content-editor',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    ContentManagersComponent,
  ],
  providers: [],
  templateUrl: './content-editor.component.html',
  styleUrl: './content-editor.component.scss',
})
export class ContentEditorComponent implements OnDestroy {
  status: string = '-1';
  type: ContentTypeValue = 'publico';
  contentData!: Content;
  subscription: Subscription = new Subscription();

  contentForm!: FormGroup;
  contentTypeList!: SelectData[];
  stateList!: SelectData[];
  content!: string;
  disabledSave: boolean = true;
  screenName = '';

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _contentUpdateService: ContentUpdateService,
    private readonly _spinnerService: SpinnerService,
    private readonly _router: Router,
    private readonly _modalService: ModalService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.content = this._activatedRoute.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.screenName = this._dataLayerService.screen_name ?? '';
    this.contentForm = this._formBuilder.group(
      {
        contentType: [this.type, [Validators.required]],
        state: [this.status, [Validators.required]],
        email: ['', [Validators.pattern(REGEX_EMAIL)]],
        managers: [[]],
        search: [null],
      },
      {
        validator: IsValue('email', '@mapfre'),
      }
    );

    this.contentTypeList = this._contentUpdateService.contentTypeSearchData;
    this.stateList = this._contentUpdateService.statusContentSearchData;

    this.contentForm
      .get('state')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((valor) => {
        this.disabledSave = valor !== this.status ? false : true;
        // registrar evento radio button
        this._dataLayerService.pushEventRadioButton(
          this.screenName,
          'state_change',
          this.stateList.find((item) => item.value === valor)?.label || valor
        );
      });

    this.getContent();
  }

  updateService(screenName: string): void {
    this._spinnerService.show();

    const body: ContentByIdBoby = {
      state: this.contentForm.value.state,
      email: this.contentForm.value.email,
      managers: this.contentForm.value.managers,
    };
    const sub = this._contentUpdateService
      .postContentById(this.content, body)
      .subscribe({
        next: (response) => {},
        error: (err) => {
          this._dataLayerService.pushEventSubmitError(
            screenName,
            'manage_content',
            'Update content failed',
            err.status
          );
          this._spinnerService.hide();
          this._modalService.showErrorModalService(() => {});
        },
        complete: () => {
          this._spinnerService.hide();
          this._dataLayerService.pushEventSubmit(
            screenName,
            'manage_content',
            'Update content completed'
          );

          this._modalService.showSuccessModalService(() => {
            this.handleRedirect(false);
          });
        },
      });
    this.subscription.add(sub);
  }

  onSubmit(): void {
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'save',
      'Guardar',
      'content actions'
    );
    const screenName = this._dataLayerService.screen_name ?? '';
    this._modalService.openGenericModal(
      this._contentUpdateService.updateModalData(() => {
        this.updateService(screenName);
      })
    );
  }

  async handleRedirect(sendEvent: boolean = false): Promise<void> {
    if (sendEvent) {
      this._dataLayerService.pushEventClickButton(
        this.screenName,
        'cancel',
        'Cancelar',
        'content actions'
      );
    }
    await this._router.navigate([`${RoutePaths.contentManag}`]);
  }

  getContent(): void {
    this._spinnerService.show();
    const sub = this._contentUpdateService
      .getContentById(this.content)
      .subscribe({
        next: (response) => {
          const contentResponse: ContentByIdResponse = response;
          this.contentData = contentResponse.content;
          this.status = contentResponse.content.state.toString();
          this.type =
            contentResponse.content.public == true ? 'publico' : 'privado';
          this.contentForm
            .get('state')
            ?.setValue(this.status, { emitEvent: false });
          this.contentForm
            .get('contentType')
            ?.setValue(this.type, { emitEvent: false });

          this.contentForm.get('managers')?.setValue(contentResponse.managers);
          this.contentForm
            .get('email')
            ?.setValue(contentResponse.content.email || '');
        },
        error: (err) => {
          this._spinnerService.hide();
        },
        complete: () => {
          this._spinnerService.hide();
        },
      });
    this.subscription.add(sub);
  }

  getManagersList(event: {
    managers: Manager[];
    operation: 'email' | 'add' | 'delete' | 'search';
  }): void {
    this.disabledSave = false;
    if (event.operation === 'email') {
      this._dataLayerService.pushEventUpdate(
        this.screenName,
        'email_change',
        'updated Email'
      );
    } else if (event.operation === 'add') {
      this._dataLayerService.pushEventUpdate(
        this.screenName,
        'managers_change',
        'added Manager'
      );
    } else if (event.operation === 'delete') {
      this._dataLayerService.pushEventUpdate(
        this.screenName,
        'managers_change',
        'deleted Manager'
      );
    } else if (event.operation === 'search') {
      this._dataLayerService.pushEventSearch(
        this.screenName,
        'search_managers',
        `managers`
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
